import { InboxAccountSchema, InboxPermissionSchema } from "@billingnow/zod-schema";
import { apiClient } from "./client";

export type InboxAccount = InboxAccountSchema;
export type InboxPermission = InboxPermissionSchema;

/**
 * Get all connected inbox accounts for the organization
 */
export const getInboxAccounts = async (): Promise<InboxAccount[]> => {
  const response = await apiClient.get<{ accounts: InboxAccount[] }>("/api/inbox");
  return response.data.accounts;
};

/**
 * Get the Gmail OAuth URL for the selected permissions.
 * Redirect user to this URL to connect (or re-connect with more permissions).
 */
export interface InboxProvidersAvailability {
  gmail: boolean;
  microsoft: boolean;
}

/** Which inbox providers are configured server-side */
export const getInboxProviders = async (): Promise<InboxProvidersAvailability> => {
  const response = await apiClient.get<InboxProvidersAvailability>("/api/inbox/providers");
  return response.data;
};

export const getMicrosoftInboxAuthUrl = async (
  permissions: InboxPermission[],
  redirect?: "settings" | "onboarding" | "dashboard",
): Promise<string> => {
  const response = await apiClient.get<{ url: string }>("/api/inbox/microsoft/auth", {
    params: { permissions: permissions.join(","), ...(redirect ? { redirect } : {}) },
  });
  return response.data.url;
};

export const getGmailInboxAuthUrl = async (
  permissions: InboxPermission[],
  redirect?: "settings" | "onboarding" | "dashboard",
): Promise<string> => {
  const response = await apiClient.get<{ url: string }>("/api/inbox/gmail/auth", {
    params: { permissions: permissions.join(","), ...(redirect ? { redirect } : {}) },
  });
  return response.data.url;
};

/**
 * Pause / resume expense ingestion for an account
 */
export const setInboxAccountPaused = async (accountId: string, paused: boolean): Promise<InboxAccount> => {
  const response = await apiClient.post<{ account: InboxAccount }>(`/api/inbox/${accountId}/paused`, { paused });
  return response.data.account;
};

/**
 * Disable one permission on an account (enabling goes through the OAuth URL)
 */
export const disableInboxAccountPermission = async (
  accountId: string,
  permission: InboxPermission,
): Promise<InboxAccount> => {
  const response = await apiClient.post<{ account: InboxAccount }>(`/api/inbox/${accountId}/permission`, {
    permission,
    enabled: false,
  });
  return response.data.account;
};

/**
 * Disconnect an inbox account (revokes the token at the provider)
 */
export const disconnectInboxAccount = async (accountId: string): Promise<void> => {
  await apiClient.delete(`/api/inbox/${accountId}`);
};

export default getInboxAccounts;
